import { commandsCtx, parserCtx } from "@milkdown/core";
import { computeDocDiff, startDiffReviewFromDocCmd } from "@milkdown/plugin-diff";
import { Fragment, Slice } from "@milkdown/prose/model";
import { $command, $ctx, $prose } from "@milkdown/utils";
import { Plugin, PluginKey } from "@milkdown/prose/state";
//#region src/__internal__/with-meta.ts
function withMeta(plugin, meta) {
	Object.assign(plugin, { meta: {
		package: "@milkdown/plugin-streaming",
		...meta
	} });
	return plugin;
}
//#endregion
//#region src/streaming-config.ts
var streamingConfig = $ctx({
	throttleMs: 100,
	scrollFollow: true,
	diffReviewOnEnd: false,
	ignoreAttrs: { heading: ["id"] }
}, "streamingConfig");
withMeta(streamingConfig, {
	displayName: "Ctx<streamingConfig>",
	group: "Streaming"
});
//#endregion
//#region src/flush.ts
function flushBuffer(ctx, tr, buffer) {
	const newDoc = ctx.get(parserCtx)(buffer);
	if (!newDoc) return {
		tr,
		newDoc: null
	};
	const ignoreAttrs = ctx.get(streamingConfig.key).ignoreAttrs;
	const changes = computeDocDiff(tr.doc, newDoc, ignoreAttrs ? { ignoreAttrs } : void 0);
	for (let i = changes.length - 1; i >= 0; i--) {
		const change = changes[i];
		const newContent = newDoc.slice(change.fromB, change.toB);
		tr = tr.replace(change.fromA, change.toA, newContent);
	}
	return {
		tr,
		newDoc
	};
}
function defaultInsertStrategy(resolved) {
	if (resolved.parent.type.spec.code) return {
		type: "plain-text",
		preserveNewlines: true
	};
	for (let d = resolved.depth; d > 0; d--) if (resolved.node(d).type.name === "table") return {
		type: "plain-text",
		preserveNewlines: false
	};
	if (resolved.depth >= 1) return { type: "split-block" };
	return { type: "block" };
}
function stripTrailingEmptyParagraph(content) {
	if (content.childCount === 0) return content;
	const last = content.lastChild;
	if (last.type.name === "paragraph" && last.content.size === 0) return content.cut(0, content.size - last.nodeSize);
	return content;
}
function flushBufferInsert(ctx, tr, options) {
	const { insertPos, currentEndPos } = options;
	const buffer = options.buffer.replace(/\r\n?/g, "\n");
	if (!buffer) return {
		tr,
		applied: false,
		insertEndPos: currentEndPos
	};
	const docSize = tr.doc.content.size;
	const clampedPos = Math.max(0, Math.min(insertPos, docSize));
	const clampedEnd = Math.max(clampedPos, Math.min(currentEndPos, docSize));
	const resolved = tr.doc.resolve(clampedPos);
	const strategy = (ctx.get(streamingConfig.key).insertStrategy ?? defaultInsertStrategy)(resolved);
	const args = {
		ctx,
		tr,
		buffer,
		from: clampedPos,
		to: clampedEnd,
		resolved
	};
	switch (strategy.type) {
		case "plain-text": return applyPlainText(args, strategy.preserveNewlines ?? false);
		case "split-block": return applySplitBlock(args);
		case "block": return applyBlock(args);
	}
}
function applyPlainText({ tr, buffer, from, to }, preserveNewlines) {
	const textContent = preserveNewlines ? buffer : buffer.replace(/\n/g, " ");
	const textNode = tr.doc.type.schema.text(textContent);
	tr = tr.replaceWith(from, to, textNode);
	return {
		tr,
		applied: true,
		insertEndPos: from + textContent.length
	};
}
var INLINE_MARKDOWN_TOKENS = /[*_~`[\\<]/;
function parseInlineContent(ctx, schema, text) {
	if (!text) return Fragment.empty;
	if (!INLINE_MARKDOWN_TOKENS.test(text)) return Fragment.from(schema.text(text));
	const firstBlock = ctx.get(parserCtx)(text)?.firstChild;
	if (firstBlock?.type.name !== "paragraph" || firstBlock.content.size === 0) return Fragment.from(schema.text(text));
	let hasInlineStructure = false;
	firstBlock.content.forEach((child) => {
		if (!child.isText || child.marks.length > 0) hasInlineStructure = true;
	});
	if (!hasInlineStructure) return Fragment.from(schema.text(text));
	let content = firstBlock.content;
	const leading = /^\s+/.exec(text)?.[0];
	if (leading) {
		if (!(content.firstChild?.isText ? content.firstChild.text ?? "" : "").startsWith(leading)) content = Fragment.from(schema.text(leading)).append(content);
	}
	const trailing = /\s+$/.exec(text)?.[0];
	if (trailing) {
		if (!(content.lastChild?.isText ? content.lastChild.text ?? "" : "").endsWith(trailing)) content = content.append(Fragment.from(schema.text(trailing)));
	}
	return content;
}
function applySplitBlock({ ctx, tr, buffer, from, to, resolved }) {
	const schema = tr.doc.type.schema;
	const firstNewline = buffer.indexOf("\n");
	if (firstNewline < 0) {
		const inlineContent = parseInlineContent(ctx, schema, buffer);
		tr = tr.replaceWith(from, to, inlineContent);
		return {
			tr,
			applied: true,
			insertEndPos: from + inlineContent.size
		};
	}
	const inlinePart = buffer.substring(0, firstNewline);
	const blockPart = buffer.substring(firstNewline + 1);
	const parser = ctx.get(parserCtx);
	const parsed = blockPart.trim() ? parser(blockPart) : null;
	const blockContent = parsed ? stripTrailingEmptyParagraph(parsed.content) : Fragment.empty;
	if (blockContent.childCount === 0) {
		const inlineContent = parseInlineContent(ctx, schema, buffer.replace(/\n/g, " "));
		tr = tr.replaceWith(from, to, inlineContent);
		return {
			tr,
			applied: true,
			insertEndPos: from + inlineContent.size
		};
	}
	const depth = resolved.depth;
	let innerContent = parseInlineContent(ctx, schema, inlinePart);
	for (let d = depth; d > 0; d--) innerContent = Fragment.from(resolved.node(d).copy(innerContent));
	const fullContent = innerContent.append(blockContent);
	const slice = new Slice(fullContent, depth, 0);
	const docSize = tr.doc.content.size;
	const resolvedTo = tr.doc.resolve(Math.min(to, docSize));
	let actualTo = to;
	if (depth > 0 && resolvedTo.depth === depth && resolvedTo.parentOffset === resolvedTo.parent.content.size) actualTo = resolvedTo.after(depth);
	tr = tr.replace(from, actualTo, slice);
	const insertedSize = fullContent.size - depth;
	return {
		tr,
		applied: true,
		insertEndPos: from + insertedSize
	};
}
function applyBlock({ ctx, tr, buffer, from, to }) {
	const parsed = ctx.get(parserCtx)(buffer);
	if (!parsed) return {
		tr,
		applied: false,
		insertEndPos: to
	};
	const content = stripTrailingEmptyParagraph(parsed.content);
	if (content.size === 0) return {
		tr,
		applied: false,
		insertEndPos: to
	};
	tr = tr.replace(from, to, new Slice(content, 0, 0));
	return {
		tr,
		applied: true,
		insertEndPos: from + content.size
	};
}
function performFlush(ctx, tr, streamingState) {
	if (streamingState.insertPos != null) {
		const insertPos = streamingState.insertPos;
		const result = flushBufferInsert(ctx, tr, {
			buffer: streamingState.buffer,
			insertPos,
			currentEndPos: streamingState.insertEndPos ?? insertPos
		});
		return {
			tr: result.tr,
			newDoc: result.applied ? result.tr.doc : null,
			insertEndPos: result.insertEndPos
		};
	}
	return flushBuffer(ctx, tr, streamingState.buffer);
}
//#endregion
//#region src/streaming-plugin.ts
var streamingPluginKey = new PluginKey("MILKDOWN_STREAMING");
function applyStreamingAction(state, action) {
	if (!state && action?.type !== "start") return null;
	if (!action) return state;
	switch (action.type) {
		case "start": return {
			buffer: "",
			originalDoc: action.originalDoc,
			active: true,
			lastApplyTime: action.lastApplyTime,
			insertPos: action.insertPos ?? null,
			insertEndPos: action.insertEndPos ?? action.insertPos ?? null
		};
		case "push":
			if (!state) return null;
			return {
				...state,
				buffer: state.buffer + action.token
			};
		case "apply":
			if (!state) return null;
			return {
				...state,
				lastApplyTime: action.lastApplyTime,
				insertEndPos: action.insertEndPos ?? state.insertEndPos
			};
		case "end":
		case "abort": return null;
		default: return state;
	}
}
var streamingPlugin = $prose((ctx) => {
	const config = ctx.get(streamingConfig.key);
	return new Plugin({
		key: streamingPluginKey,
		state: {
			init: () => null,
			apply(tr, value) {
				return applyStreamingAction(value, tr.getMeta(streamingPluginKey));
			}
		},
		filterTransaction(tr, editorState) {
			if (!streamingPluginKey.getState(editorState)?.active) return true;
			if (tr.getMeta(streamingPluginKey)) return true;
			if (tr.docChanged) return false;
			return true;
		},
		view() {
			return createFlushController(ctx, config);
		}
	});
});
withMeta(streamingPlugin, {
	displayName: "Prose<streaming>",
	group: "Streaming"
});
/**
* Creates the view controller that manages the throttled flush loop.
* It watches for buffer growth and periodically parses + diffs + applies.
*/
function createFlushController(ctx, config) {
	let trailingTimer = null;
	let lastKnownBufferLen = -1;
	let view = null;
	function flush() {
		if (!view) return;
		const state = streamingPluginKey.getState(view.state);
		if (!state?.active || state.buffer.length === lastKnownBufferLen) return;
		lastKnownBufferLen = state.buffer.length;
		const result = performFlush(ctx, view.state.tr, state);
		if (!result.newDoc) return;
		let tr = result.tr;
		if (!tr.docChanged && result.insertEndPos == null) return;
		tr = tr.setMeta(streamingPluginKey, {
			type: "apply",
			lastApplyTime: Date.now(),
			insertEndPos: result.insertEndPos
		});
		if (config.scrollFollow) tr = tr.scrollIntoView();
		view.dispatch(tr);
	}
	function scheduleTrailingFlush() {
		if (trailingTimer != null) clearTimeout(trailingTimer);
		trailingTimer = setTimeout(flush, config.throttleMs);
	}
	return {
		update(updatedView) {
			view = updatedView;
			const state = streamingPluginKey.getState(updatedView.state);
			if (!state?.active) {
				if (trailingTimer != null) {
					clearTimeout(trailingTimer);
					trailingTimer = null;
				}
				lastKnownBufferLen = -1;
				return;
			}
			if (state.buffer.length !== lastKnownBufferLen) if (Date.now() - state.lastApplyTime >= config.throttleMs) flush();
			else scheduleTrailingFlush();
		},
		destroy() {
			if (trailingTimer != null) {
				clearTimeout(trailingTimer);
				trailingTimer = null;
			}
			view = null;
		}
	};
}
//#endregion
//#region src/streaming-commands.ts
var startStreamingCmd = $command("StartStreaming", () => {
	return (options) => (state, dispatch) => {
		if (streamingPluginKey.getState(state)?.active) return false;
		if (dispatch) {
			let insertPos;
			let insertEndPos;
			if (options?.insertAt != null) if (options.insertAt === "selection") {
				insertPos = state.selection.from;
				insertEndPos = state.selection.to;
				if (state.selection.empty) {
					const resolved = state.doc.resolve(insertPos);
					if (resolved.parent.isTextblock && !resolved.parent.type.spec.code && resolved.parent.content.size === 0 && resolved.depth === 1) {
						insertPos = resolved.before(resolved.depth);
						insertEndPos = resolved.after(resolved.depth);
					}
				}
			} else {
				const rawPos = options.insertAt === "cursor" ? state.selection.head : options.insertAt;
				if (!Number.isFinite(rawPos)) return false;
				insertPos = Math.max(0, Math.min(Math.round(rawPos), state.doc.content.size));
				const resolved = state.doc.resolve(insertPos);
				if (resolved.parent.isTextblock && !resolved.parent.type.spec.code && resolved.parent.content.size === 0 && resolved.depth === 1) {
					insertPos = resolved.before(resolved.depth);
					insertEndPos = resolved.after(resolved.depth);
				}
			}
			dispatch(state.tr.setMeta(streamingPluginKey, {
				type: "start",
				originalDoc: state.doc,
				insertPos,
				insertEndPos,
				lastApplyTime: Date.now()
			}));
		}
		return true;
	};
});
withMeta(startStreamingCmd, {
	displayName: "Command<startStreaming>",
	group: "Streaming"
});
var pushChunkCmd = $command("PushChunk", () => {
	return (token) => (state, dispatch) => {
		if (token == null) return false;
		if (!streamingPluginKey.getState(state)?.active) return false;
		if (dispatch) dispatch(state.tr.setMeta(streamingPluginKey, {
			type: "push",
			token
		}));
		return true;
	};
});
withMeta(pushChunkCmd, {
	displayName: "Command<pushChunk>",
	group: "Streaming"
});
var endStreamingCmd = $command("EndStreaming", (ctx) => {
	return (options) => (state, dispatch) => {
		const streamingState = streamingPluginKey.getState(state);
		if (!streamingState?.active) return false;
		if (dispatch) {
			const config = ctx.get(streamingConfig.key);
			const diffReview = options?.diffReview ?? config.diffReviewOnEnd;
			const result = performFlush(ctx, state.tr, streamingState);
			let tr = result.tr;
			tr = tr.setMeta(streamingPluginKey, { type: "end" });
			if (diffReview && result.newDoc) {
				const finalDoc = tr.doc;
				tr = tr.replace(0, tr.doc.content.size, new Slice(streamingState.originalDoc.content, 0, 0));
				dispatch(tr);
				try {
					ctx.get(commandsCtx).call(startDiffReviewFromDocCmd.key, finalDoc);
				} catch (e) {
					console.warn("[milkdown/streaming] diff review handoff skipped:", e);
				}
				return true;
			}
			dispatch(tr);
		}
		return true;
	};
});
withMeta(endStreamingCmd, {
	displayName: "Command<endStreaming>",
	group: "Streaming"
});
var abortStreamingCmd = $command("AbortStreaming", (ctx) => {
	return (options) => (state, dispatch) => {
		const streamingState = streamingPluginKey.getState(state);
		if (!streamingState?.active) return false;
		if (dispatch) {
			const keep = options?.keep ?? false;
			let tr = state.tr;
			if (keep) tr = performFlush(ctx, tr, streamingState).tr;
			else tr = tr.replace(0, state.doc.content.size, new Slice(streamingState.originalDoc.content, 0, 0));
			tr = tr.setMeta(streamingPluginKey, { type: "abort" });
			dispatch(tr);
		}
		return true;
	};
});
withMeta(abortStreamingCmd, {
	displayName: "Command<abortStreaming>",
	group: "Streaming"
});
//#endregion
//#region src/index.ts
var streaming = [
	streamingConfig,
	streamingPlugin,
	startStreamingCmd,
	pushChunkCmd,
	endStreamingCmd,
	abortStreamingCmd
];
//#endregion
export { abortStreamingCmd, applyStreamingAction, defaultInsertStrategy, endStreamingCmd, pushChunkCmd, startStreamingCmd, streaming, streamingConfig, streamingPlugin, streamingPluginKey };

//# sourceMappingURL=index.js.map